import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { useCachedSearchTopOffers } from 'components/Accommodation/Offers/useCachedSearchTopOffers';
import { getMeta } from 'modules/meta/selectors';
import { getAnchorPriceNightlyPerRoom, getNumberOfRooms, getSearchParameters, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getAnchorPriceTotal } from 'modules/sapiSearch/utils';
import { getIsMultiRoomBundlesSearch } from 'modules/search/selectors';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Price } from '@daedalus/atlas/Price';
import { Text } from '@daedalus/atlas/Text';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { Offer } from '@daedalus/core/src/offer/types/offer';
import { getDisplayPrice, getNightlyPriceForAllRooms, getTotalPrice } from '@daedalus/core/src/price/business/price';
import { getLengthOfStay } from '@daedalus/core/src/utils/date';
import { getRoundingStrategy, RoundingStrategy } from '@daedalus/core/src/utils/number';
interface Props {
  hotelId: string;
  offer: Offer;
  onClickPriceDetails: () => void;
  shouldShowDealFreezeButton?: boolean;
  isCompact?: boolean;
  isPrivate?: boolean;
  isVioTagVisible?: boolean;
  roundingStrategy?: RoundingStrategy;
}
const _exp = /*#__PURE__*/() => Flex.Row;
const WhiteSpaceNowrapRow = /*#__PURE__*/styled(_exp())({
  name: "WhiteSpaceNowrapRow",
  class: "wrzu73d",
  propsAsIs: true
});
const _exp2 = /*#__PURE__*/() => WhiteSpaceNowrapRow;
const BasePriceRow = /*#__PURE__*/styled(_exp2())({
  name: "BasePriceRow",
  class: "b1vrgqfh",
  propsAsIs: true
});
const _exp3 = /*#__PURE__*/() => Text;
const PostFix = /*#__PURE__*/styled(_exp3())({
  name: "PostFix",
  class: "pumgohp",
  propsAsIs: true
});
const _exp5 = /*#__PURE__*/() => ButtonLink;
const PriceDetailsButton = /*#__PURE__*/styled(_exp5())({
  name: "PriceDetailsButton",
  class: "p7cyith",
  propsAsIs: true
});
const SecondaryPriceWrapper = /*#__PURE__*/styled('div')({
  name: "SecondaryPriceWrapper",
  class: "syilnqe",
  propsAsIs: false
});
const FlexWrapper = /*#__PURE__*/styled('div')({
  name: "FlexWrapper",
  class: "f5n27n1",
  propsAsIs: false
});
const TaxDisplay = /*#__PURE__*/styled('div')({
  name: "TaxDisplay",
  class: "t1usp9q7",
  propsAsIs: false
});
const IncludingTaxesMessage = /*#__PURE__*/styled('span')({
  name: "IncludingTaxesMessage",
  class: "i995rpy",
  propsAsIs: false
});
interface MakeGetPriceValuesProps {
  anchorPrice: number;
  anchorPriceTotal: number;
  numberOfRooms: number;
  showTotalPrices: boolean;
}

/**
 * Generate a getter for the prices to display for an offer
 */
const makeGetPriceValues = ({
  anchorPrice,
  anchorPriceTotal,
  numberOfRooms,
  showTotalPrices
}: MakeGetPriceValuesProps) => (offer: Offer) => {
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const displayPrice = getDisplayPrice(offer.prices);
  const totalPrice = getTotalPrice(displayPrice);
  const calculatedNightlyPrice = getNightlyPriceForAllRooms(displayPrice.chargeable.nightlyDisplayTotal, isMultiRoomBundlesSearch, numberOfRooms);
  const calculatedAnchorPrice = getNightlyPriceForAllRooms(anchorPrice, isMultiRoomBundlesSearch, numberOfRooms);
  return showTotalPrices ? {
    anchorPriceValue: anchorPriceTotal * numberOfRooms,
    mainPriceValue: totalPrice,
    detailsPriceValue: calculatedNightlyPrice
  } : {
    anchorPriceValue: calculatedAnchorPrice,
    mainPriceValue: calculatedNightlyPrice,
    detailsPriceValue: totalPrice
  };
};
const postFixMessages = defineMessages({
  total: {
    id: 'total',
    defaultMessage: 'total'
  },
  perNight: {
    id: 'perNight',
    defaultMessage: '/ night'
  }
});
const numberOfRoomsMessage = {
  id: 'numberOfRooms',
  defaultMessage: '{count, plural, one {1 room} other {# rooms}}'
};
const roomsThreshold = 1;
const PriceDetailsButtonWithTax = ({
  onClick,
  children
}: {
  onClick: () => void;
  children?: React.ReactNode;
}) => <PriceDetailsButton variant="info" isCompact onClick={onClick} size="sm" dataId="RoomPriceDetail">
    <Text tag="div" variant="labelS" dataId="includingTaxes">
      {children}
      <TaxDisplay>
        <IncludingTaxesMessage>
          <FormattedMessageWrapper id="includingTaxes" defaultMessage="incl tax and fees" />
        </IncludingTaxesMessage>
      </TaxDisplay>
    </Text>
  </PriceDetailsButton>;
export const RoomOfferPrice = ({
  hotelId,
  offer,
  onClickPriceDetails,
  isCompact = false,
  isPrivate = false,
  isVioTagVisible = false,
  roundingStrategy = getRoundingStrategy()
}: Props) => {
  const {
    formatMessage
  } = useIntl();
  const {
    offerEntity
  } = useCachedSearchTopOffers({
    hotelId
  });
  const anchorPrice = useSelector(state => getAnchorPriceNightlyPerRoom(state, offerEntity));
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity);
  const {
    currencyCode
  } = useSelector(getMeta);
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId));
  const showTotalPrices = useSelector(getShowTotalPrices);
  const getPriceValues = makeGetPriceValues({
    anchorPrice,
    anchorPriceTotal,
    numberOfRooms: numberOfRooms,
    showTotalPrices
  });
  const {
    anchorPriceValue,
    mainPriceValue,
    detailsPriceValue
  } = getPriceValues(offer);
  const pricePostFix = showTotalPrices ? postFixMessages.total : postFixMessages.perNight;
  const priceDetailPostFix = showTotalPrices ? postFixMessages.perNight : postFixMessages.total;
  const isMultiRoom = numberOfRooms > roomsThreshold;
  const isMainPriceDifferentFromDetailsPrice = mainPriceValue !== detailsPriceValue;
  const {
    checkIn,
    checkOut
  } = useSelector(getSearchParameters) || {};
  const lengthOfStay = checkIn && checkOut && getLengthOfStay(checkIn, checkOut);
  const renderPriceDetailsButton = () => {
    if (!isMainPriceDifferentFromDetailsPrice && lengthOfStay === 1) {
      return <PriceDetailsButtonWithTax onClick={onClickPriceDetails} />;
    }
    if (isMainPriceDifferentFromDetailsPrice && !isCompact) {
      return <PriceDetailsButtonWithTax onClick={onClickPriceDetails}>
          <SecondaryPriceWrapper>
            <FlexWrapper>
              <Price currency={currencyCode} value={detailsPriceValue} roundingStrategy={roundingStrategy} noStyle dataId="roomOfferPriceDetail" />
              <WhiteSpaceNowrapRow alignItems="baseline" justifyContent="flex-start">
                &nbsp;
                {formatMessage(priceDetailPostFix)}
                {isMultiRoom && <>,&nbsp;</>}
              </WhiteSpaceNowrapRow>
            </FlexWrapper>
            {isMultiRoom && formatMessage(numberOfRoomsMessage, {
            count: numberOfRooms
          })}
          </SecondaryPriceWrapper>
        </PriceDetailsButtonWithTax>;
    }
    return null;
  };
  return <Box display="flex" flexDirection="column" flexWrap="wrap" alignItems={isCompact ? 'flex-start' : 'flex-end'} justifyContent="center">
      <BasePriceRow alignItems="baseline" justifyContent={isCompact ? 'flex-start' : 'flex-end'} gap="s200" wrap="wrap">
        {isVioTagVisible && <Price isAnchor currency={currencyCode} value={anchorPriceValue} roundingStrategy={roundingStrategy} />}
        <WhiteSpaceNowrapRow wrap={isMultiRoom && !showTotalPrices ? 'wrap' : 'nowrap'} alignItems="baseline" justifyContent="flex-end">
          <Price isPrivate={isPrivate || isVioTagVisible} showLockIcon={false} currency={currencyCode} value={mainPriceValue} roundingStrategy={roundingStrategy} size="lg" dataId="roomOfferPriceMain" />
          <PostFix variant="bodyS">{formatMessage(pricePostFix)}</PostFix>
        </WhiteSpaceNowrapRow>
      </BasePriceRow>

      {renderPriceDetailsButton()}
    </Box>;
};

require("./RoomOfferPrice.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomOfferPrice.tsx");